import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import type {
  AgentEvent,
  AgentRunSnapshot,
  ReasoningSegment,
  RunStatus,
  TaskNodeStatus,
} from '@corner/shared';
import apiClient, { getAccessToken } from '../lib/apiClient';

// ─── Public types ───────────────────────────────

export type QueryRunConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** Structured denial from a failed retry/skip action. */
export interface NodeActionDenial {
  nodeId: string;
  action: 'retry' | 'skip';
  code: string;
  message: string;
}

export interface UseQueryRunStateOptions {
  runId: string | null;
}

export interface UseQueryRunStateResult {
  /** Current snapshot (null until first fetch completes) */
  snapshot: AgentRunSnapshot | null;
  /** SSE connection state */
  connectionState: QueryRunConnectionState;
  /** Live-streamed partial answer (before FINAL_ANSWER commits it to snapshot). */
  streamingAnswer: string;
  /** Thinking segments accumulated during the run. */
  thinkingSegments: ReasoningSegment[];
  /** Cancel the run */
  cancel: () => Promise<void>;
  /** Retry a failed node */
  retryNode: (nodeId: string) => Promise<void>;
  /** Skip a failed node */
  skipNode: (nodeId: string) => Promise<void>;
  /** Whether a node action (retry/skip) is currently in-flight */
  isNodeActionPending: (nodeId: string) => boolean;
  /** Last denial for a given node (cleared on next successful action). */
  getNodeDenial: (nodeId: string) => NodeActionDenial | undefined;
  /** Clear a node's denial. */
  clearNodeDenial: (nodeId: string) => void;
  /** Refresh snapshot from server */
  refresh: () => Promise<void>;
}

// ─── Helpers ───────────���─────────────────────────

const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set(['completed', 'failed', 'partial', 'cancelled']);

function isTerminal(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(status);
}

/** Apply an incoming AgentEvent to the local snapshot. */
function applyEvent(prev: AgentRunSnapshot, event: AgentEvent): AgentRunSnapshot {
  switch (event.type) {
    case 'CHECKLIST_INIT':
      return {
        ...prev,
        status: event.status === 'planning' ? 'running' : prev.status,
        tasks: event.tasks.length > 0 ? event.tasks : prev.tasks,
      };

    case 'CHECKLIST_UPDATE': {
      const tasks = prev.tasks.map((t) => {
        if (t.id !== event.taskId) return t;
        return {
          ...t,
          ...(event.updatedLabel ? { label: event.updatedLabel } : {}),
          ...(event.newSteps ? { visibleSteps: event.newSteps } : {}),
        };
      });
      return { ...prev, tasks };
    }

    case 'TASK_STARTED': {
      const tasks = prev.tasks.map((t) =>
        t.id === event.taskId
          ? { ...t, status: 'running' as TaskNodeStatus, startedAt: new Date().toISOString() }
          : t,
      );
      return { ...prev, status: 'running', tasks };
    }

    case 'STEP_COMPLETE': {
      const tasks = prev.tasks.map((t) => {
        if (t.id !== event.taskId) return t;
        const steps = [...t.visibleSteps];
        // Mark completed step by replacing label with result
        if (event.stepLabel && !steps.includes(event.stepLabel)) {
          steps.push(event.stepLabel);
        }
        return { ...t, visibleSteps: steps };
      });
      return { ...prev, tasks };
    }

    case 'TASK_REPLANNING': {
      const tasks = prev.tasks.map((t) =>
        t.id === event.taskId ? { ...t, status: 'replanning' as TaskNodeStatus } : t,
      );
      return { ...prev, tasks };
    }

    case 'TASK_COMPLETE': {
      const tasks = prev.tasks.map((t) =>
        t.id === event.taskId
          ? {
              ...t,
              status: 'completed' as TaskNodeStatus,
              result: event.result,
              citations: event.citations ?? t.citations,
              completedAt: new Date().toISOString(),
            }
          : t,
      );
      return { ...prev, tasks };
    }

    case 'FINAL_ANSWER':
      return {
        ...prev,
        status: 'completed',
        finalAnswer: event.answer,
        citations: event.citations ?? prev.citations,
        completedAt: new Date().toISOString(),
      };

    case 'ERROR': {
      if (event.taskId) {
        const tasks = prev.tasks.map((t) =>
          t.id === event.taskId
            ? { ...t, status: 'failed' as TaskNodeStatus, error: event.message }
            : t,
        );
        return { ...prev, tasks };
      }
      return { ...prev, status: 'failed', error: event.message };
    }

    case 'RUN_CANCELLED':
      return {
        ...prev,
        status: 'cancelled',
        cancelledAt: event.cancelledAt,
        cancelledBy: event.cancelledBy,
      };

    case 'TASK_RETRY_REQUESTED': {
      const tasks = prev.tasks.map((t) => {
        if (t.id !== event.taskId) return t;
        return {
          ...t,
          status: 'retry_pending' as TaskNodeStatus,
          retryCount: event.retryCount,
          error: null,
          completedAt: null,
          startedAt: null,
        };
      });
      return { ...prev, status: 'running', tasks };
    }

    case 'TASK_SKIPPED': {
      const tasks = prev.tasks.map((t) =>
        t.id === event.taskId
          ? { ...t, status: 'skipped' as TaskNodeStatus, completedAt: new Date().toISOString() }
          : t,
      );
      return { ...prev, tasks };
    }

    default:
      return prev;
  }
}

// ─── Hook ───────────────────��────────────────────

/** Extract a structured denial from an axios error response. */
function extractDenial(nodeId: string, action: 'retry' | 'skip', err: unknown): NodeActionDenial | null {
  if (!axios.isAxiosError(err) || !err.response) return null;
  const data = err.response.data as { error?: string; code?: string } | undefined;
  if (!data) return null;
  return {
    nodeId,
    action,
    code: data.code ?? `HTTP_${err.response.status}`,
    message: data.error ?? 'Action failed',
  };
}

export default function useQueryRunState({ runId }: UseQueryRunStateOptions): UseQueryRunStateResult {
  const [snapshot, setSnapshot] = useState<AgentRunSnapshot | null>(null);
  const [connectionState, setConnectionState] = useState<QueryRunConnectionState>('idle');
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [streamingAnswer, setStreamingAnswer] = useState('');
  const [thinkingSegments, setThinkingSegments] = useState<ReasoningSegment[]>([]);
  const [denials, setDenials] = useState<Map<string, NodeActionDenial>>(new Map());
  const esRef = useRef<EventSource | null>(null);

  // ── Fetch initial snapshot ──
  const refresh = useCallback(async () => {
    if (!runId) return;
    try {
      const res = await apiClient.get<AgentRunSnapshot>(`/api/query-runs/${runId}`);
      setSnapshot(res.data);
    } catch {
      // ignore — snapshot may not be ready yet
    }
  }, [runId]);

  useEffect(() => {
    if (!runId) {
      setSnapshot(null);
      setConnectionState('idle');
      setStreamingAnswer('');
      setThinkingSegments([]);
      return;
    }
    setStreamingAnswer('');
    setThinkingSegments([]);
    void refresh();
  }, [runId, refresh]);

  // ── SSE subscription ──
  useEffect(() => {
    if (!runId) return;
    const token = getAccessToken();
    if (!token) return;

    setConnectionState('connecting');
    const url = `/api/query-runs/${runId}/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => setConnectionState('open');

    es.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data) as AgentEvent;

        // Handle streaming events that live outside snapshot
        if (event.type === 'FINAL_ANSWER_TOKEN') {
          setStreamingAnswer((prev) => prev + event.token);
          return;
        }
        if (event.type === 'THINKING_SEGMENT') {
          setThinkingSegments((prev) => [...prev, event.segment]);
          return;
        }

        // FINAL_ANSWER commits the streamed answer into the snapshot — clear streaming state
        if (event.type === 'FINAL_ANSWER') {
          setStreamingAnswer('');
        }

        setSnapshot((prev) => {
          if (!prev) return prev;
          const next = applyEvent(prev, event);
          if (isTerminal(next.status) && esRef.current) {
            esRef.current.close();
            esRef.current = null;
            setConnectionState('closed');
          }
          return next;
        });
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      setConnectionState('error');
      es.close();
      esRef.current = null;
    };

    return () => {
      es.close();
      esRef.current = null;
      setConnectionState('closed');
    };
  }, [runId]);

  // ── Actions ──
  const cancel = useCallback(async () => {
    if (!runId) return;
    try {
      const res = await apiClient.post<AgentRunSnapshot>(`/api/query-runs/${runId}/cancel`);
      setSnapshot(res.data);
    } catch {
      // handled by apiClient interceptor
    }
  }, [runId]);

  const retryNode = useCallback(async (nodeId: string) => {
    if (!runId) return;
    setPendingActions((prev) => new Set(prev).add(nodeId));
    setDenials((prev) => { const next = new Map(prev); next.delete(nodeId); return next; });
    try {
      const res = await apiClient.post<AgentRunSnapshot>(
        `/api/query-runs/${runId}/nodes/${nodeId}/retry`,
        {},
        { skipGlobalErrorBanner: true } as never,
      );
      setSnapshot(res.data);
    } catch (err) {
      const denial = extractDenial(nodeId, 'retry', err);
      if (denial) {
        setDenials((prev) => new Map(prev).set(nodeId, denial));
      }
    } finally {
      setPendingActions((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    }
  }, [runId]);

  const skipNode = useCallback(async (nodeId: string) => {
    if (!runId) return;
    setPendingActions((prev) => new Set(prev).add(nodeId));
    setDenials((prev) => { const next = new Map(prev); next.delete(nodeId); return next; });
    try {
      const res = await apiClient.post<AgentRunSnapshot>(
        `/api/query-runs/${runId}/nodes/${nodeId}/skip`,
        {},
        { skipGlobalErrorBanner: true } as never,
      );
      setSnapshot(res.data);
    } catch (err) {
      const denial = extractDenial(nodeId, 'skip', err);
      if (denial) {
        setDenials((prev) => new Map(prev).set(nodeId, denial));
      }
    } finally {
      setPendingActions((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    }
  }, [runId]);

  const isNodeActionPending = useCallback(
    (nodeId: string) => pendingActions.has(nodeId),
    [pendingActions],
  );

  const getNodeDenial = useCallback(
    (nodeId: string) => denials.get(nodeId),
    [denials],
  );

  const clearNodeDenial = useCallback(
    (nodeId: string) => setDenials((prev) => { const next = new Map(prev); next.delete(nodeId); return next; }),
    [],
  );

  return { snapshot, connectionState, streamingAnswer, thinkingSegments, cancel, retryNode, skipNode, isNodeActionPending, getNodeDenial, clearNodeDenial, refresh };
}
